import React from 'react';
import { useNavigate } from 'react-router-dom';
import { FileText, Scale, AlertCircle, XCircle, DollarSign, RefreshCw, Mail, ShieldAlert, UserX } from 'lucide-react';
import { SEO } from './SEO';

export const TermsView: React.FC = () => {
  const navigate = useNavigate();

  const styles = {
    sectionTitle: 'font-medium text-2xl text-brand-black mb-4 flex items-center gap-3',
    card: 'bg-white border border-brand-border rounded-xl shadow-sm p-8 mb-8',
    iconBg: 'p-2 bg-brand-surface text-brand-black border border-brand-border rounded-lg',
  };

  return (
    <>
      <SEO
        title="Terms of Service - Five Dollar Workday"
        description="Terms of Service for Five Dollar Workday. Clear, transparent terms for using our developer productivity products."
      />
      
      <main className="flex-1 w-full max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12 text-brand-black">
        
        {/* Hero */}
        <section className="py-12 mb-12 border-b border-brand-border bg-brand-surface -mx-4 px-4 sm:-mx-6 sm:px-6 lg:-mx-8 lg:px-8">
          <h1 className="text-4xl md:text-5xl tracking-tight leading-tight mb-4 font-semibold text-brand-black">
            Terms of Service
          </h1>
          <p className="text-lg text-brand-charcoal font-light">
            Last Updated: <span className="font-medium text-brand-black">January 2026</span>
          </p>
        </section>

        {/* Introduction */}
        <section className="mb-8">
          <div className="p-6 border border-brand-border bg-white rounded-xl shadow-sm">
            <div className="flex items-start gap-3">
              <Scale className="w-6 h-6 flex-shrink-0 mt-0.5 text-brand-accent" />
              <div>
                <h2 className="font-medium text-xl text-brand-black mb-2">Agreement to Terms</h2>
                <p className="text-brand-charcoal font-light leading-relaxed">
                  By accessing or using Five Dollar Workday services ("Services"), you agree to be bound by these Terms of Service ("Terms"). If you disagree with any part of these terms, you may not access our Services.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Definitions */}
        <section className={styles.card}>
          <h2 className={styles.sectionTitle}>
            <div className={styles.iconBg}>
              <FileText className="w-6 h-6" />
            </div>
            Definitions
          </h2>
          
          <ul className="space-y-3 ml-5 text-brand-charcoal font-light">
            <li><strong className="text-brand-black font-medium">"Services"</strong> refers to all products, websites, and applications provided by Five Dollar Workday.</li>
            <li><strong className="text-brand-black font-medium">"User"</strong> refers to the individual or entity using our Services.</li>
          </ul>
        </section>

        {/* Account Terms */}
        <section className={styles.card}>
          <h2 className={styles.sectionTitle}>
            <div className={styles.iconBg}>
              <UserX className="w-6 h-6" />
            </div>
            Account Terms
          </h2>
          
          <ul className="space-y-3 ml-5 text-brand-charcoal font-light">
            <li>• You must be 18 years or older to use our Services.</li>
            <li>• You are responsible for maintaining the security of your account.</li>
            <li>• You are responsible for all activity that occurs under your account.</li>
            <li>• You must provide accurate and complete information when creating an account.</li>
          </ul>
        </section>

        {/* Payment Terms */}
        <section className={styles.card}>
          <h2 className={styles.sectionTitle}>
            <div className={styles.iconBg}>
              <DollarSign className="w-6 h-6" />
            </div>
            Payment & Billing
          </h2>
          
          <ul className="space-y-3 ml-5 text-brand-charcoal font-light">
            <li>• Paid Services are billed in advance on a monthly or annual basis.</li>
            <li>• All fees are in U.S. dollars and are non-refundable except as required by law.</li>
            <li>• We use Stripe for payment processing. We do not store your payment information.</li>
            <li>• Subscriptions automatically renew unless cancelled.</li>
          </ul>
        </section>

        {/* Cancellation */}
        <section className={styles.card}>
          <h2 className={styles.sectionTitle}>
            <div className={styles.iconBg}>
              <XCircle className="w-6 h-6" />
            </div>
            Cancellation & Termination
          </h2>
          
          <h3 className="font-medium text-brand-black mb-3">Your Rights:</h3>
          <ul className="space-y-3 mb-6 ml-5 text-brand-charcoal font-light">
            <li>• You may cancel your subscription at any time.</li>
            <li>• Cancellation takes effect at the end of the current billing period.</li>
            <li>• You may delete your account permanently at any time.</li>
          </ul>

          <h3 className="font-medium text-brand-black mb-3">Our Rights:</h3>
          <ul className="space-y-3 ml-5 text-brand-charcoal font-light">
            <li>• We may suspend or terminate your account if you violate these Terms.</li>
            <li>• We may terminate inactive free accounts after 12 months.</li>
          </ul>
        </section>

        {/* Content & IP */}
        <section className={styles.card}>
          <h2 className={styles.sectionTitle}>
            <div className={styles.iconBg}>
              <FileText className="w-6 h-6" />
            </div>
            Content & Intellectual Property
          </h2>
          
          <h3 className="font-medium text-brand-black mb-3">Your Content:</h3>
          <ul className="space-y-3 mb-6 ml-5 text-brand-charcoal font-light">
            <li>• You retain all rights to Content you upload.</li>
            <li>• You grant us a license to host and display your Content to provide the Services.</li>
          </ul>

          <h3 className="font-medium text-brand-black mb-3">Our Content:</h3>
          <ul className="space-y-3 ml-5 text-brand-charcoal font-light">
            <li>• All code, design, and documentation is owned by Five Dollar Workday.</li>
            <li>• "Five Dollar Workday" and our logo are trademarks.</li>
          </ul>
        </section>

        {/* Acceptable Use */}
        <section className={styles.card}>
          <h2 className={styles.sectionTitle}>
            <div className={styles.iconBg}>
              <ShieldAlert className="w-6 h-6" />
            </div>
            Acceptable Use Policy
          </h2>
          
          <p className="mb-4 text-brand-charcoal font-light">You agree NOT to use our Services to:</p>
          <ul className="space-y-3 ml-5 text-brand-charcoal font-light">
            <li>• Violate any laws or regulations</li>
            <li>• Infringe on intellectual property rights</li>
            <li>• Transmit malware or viruses</li>
            <li>• Attempt to gain unauthorized access</li>
            <li>• Engage in fraudulent activity</li>
          </ul>
        </section>

        {/* Liability */}
        <section className={styles.card}>
          <h2 className={styles.sectionTitle}>
            <div className={styles.iconBg}>
              <Scale className="w-6 h-6" />
            </div>
            Warranties & Limitation of Liability
          </h2>
          
          <div className="p-4 mb-6 border-l-4 border-brand-accent bg-brand-surface">
            <p className="font-bold mb-2 uppercase text-xs tracking-wider text-brand-black">Important Legal Notice</p>
            <p className="text-sm text-brand-charcoal">
              OUR SERVICES ARE PROVIDED "AS IS" WITHOUT WARRANTIES OF ANY KIND.
            </p>
          </div>
          
          <ul className="space-y-3 ml-5 text-brand-charcoal font-light">
            <li>• We do not guarantee uninterrupted service.</li>
            <li>• We are not liable for indirect or consequential damages.</li>
            <li>• Liability is limited to the amount paid in the last 12 months.</li>
          </ul>
        </section>

        {/* Contact */}
        <section className="p-8 bg-brand-black rounded-xl text-white shadow-lg">
          <h2 className="text-2xl font-medium mb-4 flex items-center gap-3">
            <Mail className="w-6 h-6 text-brand-accent" />
            Questions?
          </h2>
          
          <p className="mb-6 text-brand-gray font-light">
            If you have any questions about these Terms, please contact us.
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4">
            <a 
              href="mailto:fivedollarworkday@gmail.com" 
              className="inline-flex items-center justify-center px-6 py-3 font-medium bg-brand-accent text-white rounded-xl hover:bg-violet-700 transition-colors shadow-glow"
            >
              <Mail className="w-5 h-5 mr-2" />
              Contact Us
            </a>
            <button 
              onClick={() => navigate('/')}
              className="inline-flex items-center justify-center px-6 py-3 font-medium bg-white/10 text-white rounded-xl hover:bg-white/20 border border-white/20 transition-colors"
            >
              Back to Home
            </button>
          </div>
        </section>

      </main>
    </>
  );
};